import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import { 
  FileText,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Calendar,
  Target,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Building2,
  Clock,
  Award,
  ArrowLeft
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, ReferenceLine } from "recharts";

const StatCard = ({ icon: Icon, label, value, subValue, trend }) => (
  <div className="p-4 bg-muted/30 rounded-lg">
    <div className="flex items-center gap-2 text-muted-foreground mb-1">
      <Icon className="w-4 h-4" />
      <span className="text-xs">{label}</span>
    </div>
    <div className={`text-xl font-bold ${trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-foreground'}`}>
      {value}
    </div>
    {subValue && <div className="text-xs text-muted-foreground">{subValue}</div>}
  </div>
);

export const FundFactsheetPage = () => {
  const { schemeCode } = useParams();
  const [factsheet, setFactsheet] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFactsheet = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/funds/factsheet/${schemeCode}`);
      setFactsheet(response.data);
    } catch (error) {
      console.error("Error fetching factsheet:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFactsheet();
  }, [schemeCode]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!factsheet) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Factsheet Not Available</h2>
          <p className="text-muted-foreground mt-2">Unable to load fund factsheet</p>
          <Link to="/research">
            <Button className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Research
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const { basic_info, nav_info, returns, inception, consistency, nav_chart, rolling_returns_chart, category } = factsheet;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fund-factsheet-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <Link to="/research" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Research
            </Link>
            <h1 className="text-2xl font-bold text-foreground">{basic_info.scheme_name}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge variant="outline" className="border-primary/50 text-primary">
                {category}
              </Badge>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {basic_info.fund_house}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-primary">₹{nav_info.current_nav?.toFixed(2)}</div>
            <div className="text-sm text-muted-foreground">NAV as on {nav_info.nav_date}</div>
          </div>
        </div>

        {/* 52-Week High/Low */}
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowDown className="w-4 h-4 text-red-500" />
                <div>
                  <div className="text-xs text-muted-foreground">52W Low</div>
                  <div className="font-bold">₹{nav_info.week_52_low?.toFixed(2)}</div>
                </div>
              </div>
              
              <div className="flex-1 mx-6">
                <div className="relative h-2 bg-muted rounded-full">
                  <div 
                    className="absolute h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full"
                    style={{ width: '100%' }}
                  />
                  <div 
                    className="absolute w-4 h-4 bg-primary border-2 border-white rounded-full -top-1 transform -translate-x-1/2"
                    style={{ 
                      left: `${Math.max(0, Math.min(100, ((nav_info.current_nav - nav_info.week_52_low) / (nav_info.week_52_high - nav_info.week_52_low)) * 100))}%`
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>{nav_info.from_52w_low?.toFixed(1)}% from low</span>
                  <span>{nav_info.from_52w_high?.toFixed(1)}% from high</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">52W High</div>
                  <div className="font-bold">₹{nav_info.week_52_high?.toFixed(2)}</div>
                </div>
                <ArrowUp className="w-4 h-4 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Returns Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {[
            { label: '1M', value: returns.returns1M, key: '1m' },
            { label: '3M', value: returns.returns3M, key: '3m' },
            { label: '6M', value: returns.returns6M, key: '6m' },
            { label: '1Y', value: returns.returns1Y, key: '1y' },
            { label: '3Y', value: returns.returns3Y, key: '3y' },
            { label: '5Y', value: returns.returns5Y, key: '5y' },
            { label: 'Since Inception', value: returns.since_inception, key: 'inception' }
          ].map(ret => (
            <Card key={ret.key} className="bg-card/50 border-border/50">
              <CardContent className="p-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">{ret.label}</div>
                <div className={`text-xl font-bold ${(ret.value || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {ret.value ? `${ret.value >= 0 ? '+' : ''}${ret.value.toFixed(2)}%` : '-'}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* NAV Chart */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                NAV Movement (12 Months)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={nav_chart}>
                    <defs>
                      <linearGradient id="navGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="month" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #374151' }}
                      formatter={(value) => [`₹${value.toFixed(2)}`, 'NAV']}
                    />
                    <Area type="monotone" dataKey="nav" stroke="#F59E0B" fillOpacity={1} fill="url(#navGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Rolling Returns Chart */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Rolling Returns (1Y)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={rolling_returns_chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="end_date" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #374151' }}
                      formatter={(value) => [`${value}%`, 'Return']}
                    />
                    <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="return" stroke="#F59E0B" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Key Metrics */}
        <Card className="bg-card/50 border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Key Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                icon={Award} 
                label="Consistency Score" 
                value={`${consistency.score}%`}
                subValue="Positive rolling periods"
                trend={consistency.score >= 70 ? 'up' : consistency.score < 50 ? 'down' : undefined}
              />
              <StatCard 
                icon={TrendingUp} 
                label="Avg Rolling Return" 
                value={`${consistency.avg_rolling_return}%`}
                subValue="12-month rolling"
                trend={consistency.avg_rolling_return >= 10 ? 'up' : 'down'}
              />
              <StatCard 
                icon={ArrowUp} 
                label="Best Period" 
                value={`+${consistency.best_period}%`}
                subValue="Highest 1Y return"
                trend="up"
              />
              <StatCard 
                icon={ArrowDown} 
                label="Worst Period" 
                value={`${consistency.worst_period}%`}
                subValue="Lowest 1Y return"
                trend={consistency.worst_period < 0 ? 'down' : undefined}
              />
            </div>
          </CardContent>
        </Card>

        {/* Fund Details */}
        <Card className="bg-card/50 border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Fund Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="text-sm text-muted-foreground">Scheme Type</div>
                <div className="font-medium">{basic_info.scheme_type || 'Open Ended'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Category</div>
                <div className="font-medium">{basic_info.scheme_category || category}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Inception Date</div>
                <div className="font-medium">{inception.date}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Inception NAV</div>
                <div className="font-medium">₹{inception.nav?.toFixed(2)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4">
          <Link to={`/research/${schemeCode}`}>
            <Button variant="outline">
              View Full Details <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          </Link>
          <Link to={`/returns-calculator`}>
            <Button>
              Calculate Returns <TrendingUp className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};
